import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Plus, Zap, Shield, Activity, Wrench, Download, Printer, Edit, Eye, Camera, MapPin, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from './ui/label';
import TestReportFillForm from './iso9001/TestReportFillForm';
import { SearchSortBar, CREATED_SORT_OPTIONS, matchesQuery, matchesWeek, byCreated, byText } from './ListSearchSort';
import { useSavedView } from '../hooks/useSavedView';
import { loadDocs, createDoc, isLocalId } from '../lib/offlineDocs';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import useDeepShortcut from '../hooks/useDeepShortcut';
import usePermissions from '../hooks/usePermissions';
import { OfflineNotice, PendingSyncBadge } from './OfflineNotice';
import { DateText } from './ui/week-badge';

const TPL_CACHE_KEY = 'expertfix-test-report-templates';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api`;

const TYPE_META = {
  continuity: { label: i18n.t('auto.lbl.earthContinuity'), icon: Activity, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  insulation: { label: i18n.t('auto.lbl.insulationResistance'), icon: Shield, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  fault_loop: { label: i18n.t('auto.lbl.earthFaultLoop'), icon: Zap, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  rcd: { label: i18n.t('auto.lbl.rcdTest'), icon: Zap, color: 'bg-red-50 text-red-700 border-red-200' },
  torque: { label: i18n.t('auto.lbl.torqueTightening'), icon: Wrench, color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  conditional_approval: 'bg-orange-100 text-orange-700',
  signed_off: 'bg-emerald-100 text-emerald-700',
};

const ProjectTestReports = ({ projectId, projectName, token, currentUser }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState('continuity');
  const [createTplId, setCreateTplId] = useState('');
  const [activeInstance, setActiveInstance] = useState(null);
  const [offlineList, setOfflineList] = useState(false);
  const isOnline = useOnlineStatus();
  const [filterLocation, setFilterLocation] = useState('');
  const { search, setSearch, sortBy, setSortBy } = useSavedView('project-test-reports');
  const [week, setWeek] = useState(null);

  // Distinct equipment refs present on this project's reports. We use
  // ONLY machine/panel ID (NOT installation_site, which is project-wide
  // address and therefore the same on every report — useless for
  // filtering). Tasks and Checklists use the same "equipment ref"
  // concept for their Location filter.
  const locationOf = (i) => (i.header_values || {}).machine_panel_id || '';

  // Dashboard shortcuts: pin the open report, or the New report picker.
  useDeepShortcut({
    active: !!activeInstance,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'test-reports', focusReportId: activeInstance?.id },
    label: activeInstance?.report_number,
    subtitle: projectName || '',
    icon: 'testReports',
    perms: ['projects.view'],
    priority: 3,
    focusKey: 'focusReportId',
    onFocus: (id) => {
      const found = instances.find((i) => i.id === id);
      if (found) setActiveInstance(found);
    },
  });
  useDeepShortcut({
    active: createOpen,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'test-reports', create: 'test_report' },
    label: t('auto.lbl.newTestReport'),
    subtitle: projectName || '',
    icon: 'testReports',
    perms: ['projects.view'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'test_report') setCreateOpen(true); },
  });
  const locationOptions = useMemo(() => {
    const set = new Set();
    for (const i of instances) {
      const l = locationOf(i);
      if (l) set.add(l);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [instances]);
  const REPORT_SORTS = {
    created_desc: byCreated('desc'),
    created_asc: byCreated('asc'),
    number: byText((i) => i.report_number),
    type: byText((i) => (TYPE_META[i.test_type] || {}).label),
    status: byText((i) => i.status),
    test_date: (a, b) => String((b.header_values || {}).date_of_test || '')
      .localeCompare(String((a.header_values || {}).date_of_test || '')),
  };
  const filteredInstances = useMemo(() => {
    const fl = filterLocation.toLowerCase();
    const list = instances.filter((i) => {
      if (filterLocation && locationOf(i).toLowerCase() !== fl) return false;
      const hv = i.header_values || {};
      if (!matchesWeek(week, hv.date_of_test || i.created_at)) return false;
      return matchesQuery(search, [
        i.report_number, i.doc_ref, i.template_name, i.status,
        (TYPE_META[i.test_type] || {}).label, i.test_type,
        i.created_by_name, hv.machine_panel_id, hv.tested_by_name,
        hv.installation_site, hv.date_of_test,
      ]);
    });
    return [...list].sort(REPORT_SORTS[sortBy] || REPORT_SORTS.created_desc);
    /* eslint-disable-next-line */
  }, [instances, filterLocation, search, sortBy, week]);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    // Templates are small JSON and needed to start a report with no
    // signal, so they're mirrored in localStorage.
    try {
      const tpls = await axios.get(`${API}/test-reports/templates`, authHeader);
      setTemplates(tpls.data);
      try { localStorage.setItem(TPL_CACHE_KEY, JSON.stringify(tpls.data)); } catch { /* quota */ }
    } catch {
      try { setTemplates(JSON.parse(localStorage.getItem(TPL_CACHE_KEY) || '[]')); } catch { setTemplates([]); }
    }
    try {
      const { rows, offline } = await loadDocs('test_report', projectId, async () => (
        (await axios.get(`${API}/test-reports/instances?project_id=${projectId}`, authHeader)).data
      ));
      setInstances(rows);
      setOfflineList(offline);
    } catch { toast.error(t('auto.misc.failedToLoadTestReports')); }
    finally { setLoading(false); }
  }, [projectId, authHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const templatesForType = useMemo(
    () => templates.filter(t => t.test_type === createType && t.is_active !== false),
    [templates, createType]
  );

  useEffect(() => {
    if (templatesForType.length && !templatesForType.find(t => t.id === createTplId)) {
      setCreateTplId(templatesForType[0].id);
    }
  }, [templatesForType, createTplId]);

  const handleCreate = async () => {
    if (!createTplId) { toast.error(t('auto.misc.pickATemplate')); return; }
    const tpl = templates.find((t) => t.id === createTplId);
    try {
      const { online, data } = await createDoc({
        kind: 'test_report',
        projectId,
        payload: { template_id: createTplId, project_id: projectId },
        // Offline we can't ask the server for a number or the
        // project-derived header values, so we seed a usable shell.
        docShell: {
          template_name: tpl?.name,
          test_type: tpl?.test_type,
          doc_ref: tpl?.doc_ref || '',
          revision: tpl?.revision || 'Rev 01',
          standard_reference: tpl?.standard_reference || '',
          project_name: projectName,
          header_values: {},
          rows: Array.from({ length: tpl?.default_rows || 5 }, (_, i) => ({
            row_id: `r${i}-${Date.now()}`, seq: i + 1,
          })),
          sign_offs: (tpl?.sign_off_roles || ['Tested By', 'Approved By', 'Client Representative'])
            .map((role) => ({ role, user_id: null, name: null, signed: false, signed_at: null })),
        },
        template: tpl,
        label: `Test report — ${tpl?.name || 'new'}`,
        token,
        onlineCreate: async () => (await axios.post(`${API}/test-reports/instances`, {
          template_id: createTplId, project_id: projectId,
        }, authHeader)).data,
      });
      toast.success(online
        ? `Report ${data.report_number} created`
        : 'Report started on this device — it will upload when you\'re back online');
      setCreateOpen(false);
      await fetchData();
      setActiveInstance(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Server-side clone of an existing instance → fresh draft with same
  // template + project + header_values but blanked measurements and
  // unsigned. Opens the new draft immediately for editing.
  const handleCopyInstance = async (instId, e) => {
    e?.stopPropagation();
    try {
      const resp = await axios.post(`${API}/test-reports/instances/${instId}/copy`, {}, authHeader);
      toast.success(`Copied as ${resp.data.report_number}`);
      await fetchData();
      setActiveInstance(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy failed');
    }
  };

  // Deleting a report needs Test Reports · Delete from the matrix.
  // Signed-off reports are archived from ISO 9001 instead, so the audit
  // trail survives.
  const canDelete = (inst) => {
    if (!currentUser) return false;
    if (inst.status === 'completed' || inst.status === 'signed_off') return false;
    return can('iso_test_reports.delete');
  };

  const handleDelete = async (inst, e) => {
    e?.stopPropagation();
    if (!window.confirm(
      `Delete test report ${inst.report_number || ''}?\n\n` +
      `${inst.template_name || 'This report'} and all recorded measurements will be permanently removed. This cannot be undone.`
    )) return;
    try {
      await axios.delete(`${API}/test-reports/instances/${inst.id}`, authHeader);
      toast.success(t('auto.misc.testReportDeleted'));
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleExportPDF = async (id, mono = false, withPhotos = false, e) => {    e?.stopPropagation();
    toast.info(withPhotos ? 'Generating PDF with photos…' : (mono ? t('auto.misc.generatingBWPdf') : t('auto.misc.generatingPdf')));
    try {
      const params = new URLSearchParams();
      if (mono) params.append('mono', 'true');
      if (withPhotos) params.append('with_photos', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await fetch(`${API}/test-reports/instances/${id}/pdf${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF failed');
      const blob = await resp.blob();
      const match = (resp.headers.get('Content-Disposition') || '').match(/filename="(.+)"/);
      const filename = match ? match[1] : `test_report_${id}${mono ? '_BW' : ''}${withPhotos ? '_with_photos' : ''}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = weekFileName(filename); a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  if (activeInstance) {
    return (
      <TestReportFillForm
        instance={activeInstance}
        template={templates.find(t => t.id === activeInstance.template_id)}
        token={token}
        currentUser={currentUser}
        projects={[]}
        onBack={() => { setActiveInstance(null); fetchData(); }}
        onExportPDF={(mono) => handleExportPDF(activeInstance.id, mono)}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="project-test-reports">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-gray-600">{t('auto.misc.en602041ElectricalTestReports')} <strong>{projectName}</strong></div>
        <div className="flex items-center gap-2">
          {(locationOptions.length > 0 || filterLocation) && (
            <div className="flex items-center gap-1">
              <Label className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{t('auto.misc.location')}</Label>
              <Select value={filterLocation || '__all__'} onValueChange={(v) => setFilterLocation(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-8 w-48" data-testid="test-report-location-filter"><SelectValue placeholder={t('auto.misc.all')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('auto.misc.allLocations')}</SelectItem>
                  {locationOptions.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="project-new-test-report-btn">
            <Plus className="h-4 w-4 mr-1" /> {t('auto.misc.newTestReport')}
          </Button>
        </div>
      </div>

      <SearchSortBar
        search={search}
        onSearch={setSearch}
        sortBy={sortBy}
        onSort={setSortBy}
        placeholder={t('auto.misc.searchByReportNumberTestType')}
        testIdPrefix="project-test-reports"
        week={week}
        onWeek={setWeek}
        options={[
          ...CREATED_SORT_OPTIONS,
          { value: 'number', label: t('auto.lbl.reportNumber') },
          { value: 'type', label: t('auto.lbl.testType') },
          { value: 'status', label: t('auto.lbl.status') },
          { value: 'test_date', label: t('auto.lbl.dateOfTest') },
        ]}
      />

      <OfflineNotice
        isOnline={isOnline}
        cached={offlineList}
        what="test reports"
        testId="test-reports-offline-notice"
      />

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">{t('auto.misc.loading2')}</div>
      ) : filteredInstances.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          {instances.length === 0 ? t('auto.misc.noTestReportsForThisProject') : t('auto.misc.noTestReportsMatchTheCurrent')}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredInstances.map(i => {
            const meta = TYPE_META[i.test_type] || TYPE_META.continuity;
            const Icon = meta.icon;
            const hv = i.header_values || {};
            const equipRef = hv.machine_panel_id || '';
            const testedBy = hv.tested_by_name || '';
            const testDate = hv.date_of_test;
            const isLocked = (i.sign_offs || []).every(s => s.signed) && (i.sign_offs || []).length > 0;
            return (
              <div
                key={i.id}
                className="border rounded-lg p-2.5 flex flex-col sm:flex-row sm:items-start sm:justify-between hover:bg-gray-50 hover:border-gray-300 cursor-pointer gap-1.5 sm:gap-2 transition-colors"
                onClick={() => setActiveInstance(i)}
                data-testid={`project-test-report-${i.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 min-w-0 flex-1">
                  <span className={`inline-flex w-fit items-center gap-1 text-xs px-2 py-0.5 rounded border ${meta.color} flex-shrink-0 sm:mt-0.5`}>
                    <Icon className="h-3 w-3" /> {meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold">{i.report_number}</span>
                      {isLocalId(i.id) && <PendingSyncBadge testId={`test-report-pending-${i.id}`} />}
                      {i.doc_ref && <span className="font-mono text-[10px] text-gray-400 uppercase">{i.doc_ref}</span>}
                      <Badge className={`${STATUS_COLORS[i.status] || STATUS_COLORS.draft} text-xs font-normal`}>
                        {i.status.replace('_', ' ')}
                      </Badge>
                      {isLocked && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{t('auto.misc.locked')}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{i.template_name} · {(i.rows || []).length} rows</div>
                    {equipRef && (
                      <div className="text-xs text-[#AA0000] font-medium mt-1 flex items-center gap-1">
                        <span>📍</span>
                        <span className="truncate">{equipRef}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-gray-500">
                      {testDate && (
                        <span title={t('auto.misc.dateOfTest')}><span className="text-gray-400">{t('auto.misc.tested')}</span> {testDate}</span>
                      )}
                      {!testDate && i.created_at && (
                        <span title={t('auto.misc.created')}><span className="text-gray-400">{t('auto.misc.created')}</span> <DateText value={i.created_at} /></span>
                      )}
                      {testedBy && (
                        <span title={t('auto.misc.testedBy')}><span className="text-gray-400">{t('auto.misc.by')}</span> {testedBy}</span>
                      )}
                      {i.created_by_name && i.created_by_name !== '—' && i.created_by_name !== testedBy && (
                        <span title={t('auto.misc.owner')}><span className="text-gray-400">{t('auto.misc.owner')}</span> {i.created_by_name}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1.5 flex-shrink-0 border-t pt-1 sm:border-0 sm:pt-0" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={(e) => handleCopyInstance(i.id, e)} title="Copy as new draft" data-testid={`copy-test-report-${i.id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} title={t('auto.misc.exportPdf')}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <div className="px-2 py-1"><DocLangToggle testId={`doc-lang-projecttestreports-0-${i.id}`} /></div>
                      <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, false, false, e)} data-testid={`export-pdf-color-${i.id}`}>
                        <Download className="h-4 w-4 mr-2" /> {t('auto.misc.colorPdf')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, true, false, e)} data-testid={`export-pdf-bw-${i.id}`}>
                        <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, false, true, e)} data-testid={`export-pdf-color-with-photos-${i.id}`}>
                        <Camera className="h-4 w-4 mr-2" /> {t('auto.misc.colorPhotoEvidence')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, true, true, e)} data-testid={`export-pdf-bw-with-photos-${i.id}`}>
                        <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {canDelete(i) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDelete(i, e)}
                      title={t('auto.misc.deleteTestReport')}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-test-report-${i.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Test Report for {projectName}</DialogTitle>
            <DialogDescription>{t('auto.misc.pickATestTypeAndTemplate')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">{t('auto.misc.testType')}</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {Object.entries(TYPE_META).map(([k, m]) => {
                  const Icon = m.icon;
                  const active = createType === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setCreateType(k)}
                      className={`p-2.5 rounded-lg border text-left text-xs ${active ? `${m.color} ring-2 ring-offset-1` : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      <Icon className="h-4 w-4 mb-1" />
                      <div className="font-medium">{m.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">{t('auto.misc.template')}</label>
              <Select value={createTplId} onValueChange={setCreateTplId}>
                <SelectTrigger><SelectValue placeholder={t('auto.misc.pickATemplate')} /></SelectTrigger>
                <SelectContent>
                  {templatesForType.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} · {t.doc_ref}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="dialog-cancel-btn">{t('auto.misc.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="dialog-create-btn">{t('auto.misc.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectTestReports;
